import React from "react";
import * as Icon from "react-feather";
import { formatDrillAmount, formatDrillDate } from "./helpers/formatters";
import AFSService from "../../../../../services/AFSService";
import BookkeepingService from "../../../../../services/BookkeepingService";
import UtilityService from "../../../../../services/UtilityService";
import { CANONICAL } from "../../../../../constants/CanonicalCodes";
import WComponent from "../../../../common/WComponent";
import ToolsLedgerEntries from "../../kit/ToolsLedgerEntries";
import "./statement-line-detail-panel.css";

class StatementLineDetailPanel extends WComponent {
	state = {
		trail: [this.props.opened],
		detail: null,
		loading: false,
		loadError: null,
		ledgers: {},
		attachments: {},
		panelWidth: 460,
	};

	requestNumber = 0;
	unmounted = false;

	componentDidMount() {
		this.readTheOpenedLine();
	}

	componentDidUpdate(previousProps, previousState) {
		if (previousProps.opened !== this.props.opened) {
			this.update({ trail: [this.props.opened] });
			return;
		}
		if (previousState.trail !== this.state.trail) this.readTheOpenedLine();
	}

	componentWillUnmount() {
		this.unmounted = true;
		this.stopResizing();
	}

	showing = () => this.state.trail[this.state.trail.length - 1];

	isNetIncome = opened => opened.kind === "line" && opened.canonicalCode === CANONICAL.CURRENT_YEAR_EARNINGS;

	asTheIncomeStatement = opened => {
		const incomeStatement = (this.props.statements || {}).incomeStatement || {};
		const total = AFSService.describeSectionsTotalToOpen({ title: opened.title, sections: incomeStatement.sections, current: opened.current, prior: opened.prior });
		return total;
	};

	sourceCodeFor = opened => opened.reclassifiedFrom || opened.canonicalCode;

	ledgerKey = ({ qboAccountId, period }) => qboAccountId + "|" + period;

	attachmentsKey = ({ transactionType, transactionId }) => transactionType + "|" + transactionId;

	describeLineName = canonicalCode => {
		const statements = this.props.statements || {};
		const account = AFSService.findAccountsATotalIsMadeOf({ canonicalCodes: [canonicalCode], statements })[0];
		return account ? account.description : canonicalCode;
	};

	readTheOpenedLine = async () => {
		const opened = this.showing();
		if (!opened || opened.kind !== "line" || this.isNetIncome(opened)) {
			this.update({ detail: null, loading: false, loadError: null });
			return;
		}
		const request = ++this.requestNumber;
		await this.update({ loading: true, loadError: null, detail: null });
		const answer = await BookkeepingService.describeStatementLines({ clientId: this.props.clientId, wpfEngagementId: this.props.wpfEngagementId, canonicalCodes: [this.sourceCodeFor(opened)] });
		if (this.unmounted || request !== this.requestNumber) return;
		if (!answer || answer.error) {
			this.update({ loading: false, loadError: (answer && answer.error) || "What makes up this line could not be read." });
			return;
		}
		this.update({ loading: false, detail: answer });
	};

	onOpenLine = line => this.update({ trail: this.state.trail.concat([line]) });

	onBack = () => this.update({ trail: this.state.trail.slice(0, -1) });

	onToggleLedger = async ({ qboAccountId, period }) => {
		const key = this.ledgerKey({ qboAccountId, period });
		const existing = this.state.ledgers[key];
		if (existing && !existing.loading) {
			const ledgers = { ...this.state.ledgers };
			delete ledgers[key];
			this.update({ ledgers });
			return;
		}
		await this.update({ ledgers: { ...this.state.ledgers, [key]: { loading: true } } });
		const answer = await BookkeepingService.pullQuickBooksAccountLedger({ clientId: this.props.clientId, wpfEngagementId: this.props.wpfEngagementId, qboAccountId, period });
		if (this.unmounted) return;
		const ledgerWasRead = !!(answer && answer.ledger);
		const whyNot = (answer && answer.error) || "QuickBooks's ledger could not be read.";
		const settled = ledgerWasRead ? { ledger: answer.ledger } : { error: whyNot };
		this.update({ ledgers: { ...this.state.ledgers, [key]: settled } });
	};

	onToggleAttachments = async ({ transactionType, transactionId }) => {
		const key = this.attachmentsKey({ transactionType, transactionId });
		const existing = this.state.attachments[key];
		if (existing && !existing.loading) {
			const attachments = { ...this.state.attachments };
			delete attachments[key];
			this.update({ attachments });
			return;
		}
		await this.update({ attachments: { ...this.state.attachments, [key]: { loading: true } } });
		const answer = await BookkeepingService.describeAttachmentsForLedgerEntry({ clientId: this.props.clientId, transactionType, transactionId });
		if (this.unmounted) return;
		const wasRead = !!(answer && !answer.error);
		const whyNot = (answer && answer.error) || "QuickBooks's attachments could not be read.";
		const settled = wasRead ? { checked: answer.checked, attachments: answer.attachments } : { error: whyNot };
		this.update({ attachments: { ...this.state.attachments, [key]: settled } });
	};

	onPreviewAttachment = async ({ attachment, key }) => {
		const answer = await BookkeepingService.previewLedgerEntryAttachment({ clientId: this.props.clientId, attachableId: attachment.id, fileName: attachment.fileName });
		const previewError = answer && answer.error ? answer.error : null;
		this.update({ attachments: { ...this.state.attachments, [key]: { ...this.state.attachments[key], previewError } } });
	};

	onResizeStart = event => {
		event.preventDefault();
		this.resizeStartX = event.clientX;
		this.resizeStartWidth = this.state.panelWidth;
		document.addEventListener("mousemove", this.onResizing);
		document.addEventListener("mouseup", this.stopResizing);
	};

	onResizing = event => {
		const width = this.resizeStartWidth + (this.resizeStartX - event.clientX);
		this.setState({ panelWidth: Math.max(380, Math.min(width, 960)) });
	};

	stopResizing = () => {
		document.removeEventListener("mousemove", this.onResizing);
		document.removeEventListener("mouseup", this.stopResizing);
	};

	renderFigures = ({ current, prior }) => {
		const { currentYear, priorYear } = this.props;
		return (
			<div className="afs-line__figures">
				<div className="afs-line__figure">
					<span className="afs-line__figure-label">{currentYear}</span>
					<span className="afs-line__figure-value">{formatDrillAmount(current)}</span>
				</div>
				<div className="afs-line__figure">
					<span className="afs-line__figure-label">{priorYear}</span>
					<span className="afs-line__figure-value afs-line__figure-value--prior">{formatDrillAmount(prior)}</span>
				</div>
			</div>
		);
	};

	renderLines = opened => {
		const lines = this.isNetIncome(opened) ? this.asTheIncomeStatement(opened).lines : opened.lines;
		if (lines.length === 0) return <p className="afs-line__empty">No line behind this total carries a figure.</p>;
		return (
			<div className="afs-line__section" data-test="line-detail-lines">
				<div className="afs-line__section-head">
					<span className="tk-section-title">Made up of</span>
					<span className="tk-section-count">{UtilityService.describeCount({ count: lines.length, singular: "line" })}</span>
				</div>
				<div className="tk-ledger__row tk-ledger__row--head afs-line__row afs-line__row--lines-head">
					<span>Line</span>
					<span className="tk-ledger__amount">{this.props.currentYear}</span>
					<span className="tk-ledger__amount">{this.props.priorYear}</span>
					<span />
				</div>
				{lines.map(line => (
					<button key={line.canonicalCode + "|" + (line.reclassifiedFrom || "")} type="button" className="tk-ledger__row afs-line__row afs-line__row--openable" onClick={() => this.onOpenLine(line)}>
						<span className="afs-line__row-name">{line.title}</span>
						<span className="tk-ledger__amount">{formatDrillAmount(line.current)}</span>
						<span className="tk-ledger__amount afs-line__row-amount--prior">{formatDrillAmount(line.prior)}</span>
						<Icon.ChevronRight size={14} className="afs-line__row-chevron" />
					</button>
				))}
			</div>
		);
	};

	renderHowItIsMade = ({ opened, line }) => {
		const made = AFSService.describeHowALineIsMade({ line, presentedOnTheOtherSide: !!opened.reclassifiedFrom });
		const clickedOnAStatement = typeof opened.current === "number" && !opened.presentedAs;
		const tiesToTheStatement = !clickedOnAStatement || Math.abs(made.closing - opened.current) < 0.01;
		const steps = [
			{ label: "Opening — last year's closing", amount: made.opening },
			{ label: "Movements in the year", amount: made.movements },
			{ label: "Adjusting entries", amount: made.adjustments },
		];
		return (
			<div className="afs-line__section" data-test="line-detail-made">
				<div className="afs-line__section-head">
					<span className="tk-section-title">How the figure is made</span>
				</div>
				{steps.map(step => (
					<div key={step.label} className="tk-ledger__row afs-line__row">
						<span className="afs-line__row-name">{step.label}</span>
						<span className="tk-ledger__amount">{formatDrillAmount(step.amount)}</span>
					</div>
				))}
				<div className="tk-ledger__row tk-ledger__row--total afs-line__row">
					<span className="afs-line__row-name">{opened.presentedAs ? "Balance, presented as " + this.describeLineName(opened.presentedAs) : "On the statement"}</span>
					<span className="tk-ledger__amount">{formatDrillAmount(made.closing)}</span>
				</div>
				{!tiesToTheStatement && (
					<p className="afs-line__warning">These parts add up to {formatDrillAmount(made.closing)}, but the statement shows {formatDrillAmount(opened.current)}. Re-run the compilation; if they still differ, the statement is out of date.</p>
				)}
			</div>
		);
	};

	renderLedger = ({ qboAccountId, period }) => {
		const settled = this.state.ledgers[this.ledgerKey({ qboAccountId, period })];
		if (!settled) return null;
		if (settled.loading) return <p className="tk-ledger-entries__note">Reading QuickBooks's ledger…</p>;
		if (settled.error) return <p className="tk-ledger-entries__note tk-ledger-entries__note--problem">{settled.error}</p>;
		const { ledger } = settled;
		const agreementIsAProblem = !ledger.agreesWithTheTrialBalance && !ledger.differenceIsTheAutomaticClose;
		const note = { text: AFSService.describeLedgerAgreement({ ledger }), isProblem: agreementIsAProblem };
		return (
			<ToolsLedgerEntries
				ledger={ledger}
				note={note}
				attachmentsByEntryKey={this.state.attachments}
				onToggleAttachments={this.onToggleAttachments}
				onPreviewAttachment={this.onPreviewAttachment}
			/>
		);
	};

	renderQuickBooksAccounts = ({ opened, line }) => {
		const sign = opened.reclassifiedFrom ? -1 : 1;
		const accounts = line.quickBooksAccounts;
		return (
			<div className="afs-line__section" data-test="line-detail-quickbooks">
				<div className="afs-line__section-head">
					<span className="tk-section-title">From QuickBooks</span>
					<span className="tk-section-count">{UtilityService.describeCount({ count: accounts.length, singular: "account" })}</span>
				</div>
				{accounts.length === 0 && <p className="afs-line__empty">No QuickBooks account is linked to this line; its figure comes from the entries below.</p>}
				{accounts.length > 0 && (
					<div className="tk-ledger__row tk-ledger__row--head afs-line__row">
						<span>Account</span>
						<span className="tk-ledger__amount">{this.props.currentYear}</span>
						<span className="tk-ledger__amount">{this.props.priorYear}</span>
					</div>
				)}
				{accounts.map(account => {
					const showingEntries = !!this.state.ledgers[this.ledgerKey({ qboAccountId: account.qboAccountId, period: "current" })];
					return (
						<div key={account.qboAccountId} className="afs-line__source">
							<div className="tk-ledger__row afs-line__row">
								<span className="afs-line__row-name">
									{BookkeepingService.describeChartAccount({ account })}
									<button type="button" className="tk-text-action afs-line__entries-action" data-test="line-detail-entries" onClick={() => this.onToggleLedger({ qboAccountId: account.qboAccountId, period: "current" })}>
										{showingEntries ? "Hide entries" : "Entries"}
									</button>
								</span>
								<span className="tk-ledger__amount">{formatDrillAmount(sign * account.thisYear)}</span>
								<span className="tk-ledger__amount afs-line__row-amount--prior">{formatDrillAmount(sign * account.lastYear)}</span>
							</div>
							{this.renderLedger({ qboAccountId: account.qboAccountId, period: "current" })}
						</div>
					);
				})}
			</div>
		);
	};

	renderImportedRows = ({ line }) => {
		if (line.importedRows.length === 0) return null;
		return (
			<div className="afs-line__section">
				<div className="afs-line__section-head">
					<span className="tk-section-title">From the trial balance file</span>
				</div>
				{line.importedRows.map((row, position) => (
					<div key={position} className="tk-ledger__row afs-line__row">
						<span className="afs-line__row-name">{row.description}<span className="afs-line__row-detail">{row.sourceFileName}</span></span>
						<span className="tk-ledger__amount">{row.debit ? formatDrillAmount(row.debit) : "—"}</span>
						<span className="tk-ledger__amount">{row.credit ? formatDrillAmount(row.credit) : "—"}</span>
					</div>
				))}
			</div>
		);
	};

	renderTransactions = ({ line }) => {
		if (line.transactions.length === 0) return null;
		const { rows, totalDebit, totalCredit } = BookkeepingService.buildAccountDrilldownRows({ transactions: line.transactions, invertSign: true, taxIsAmount: false });
		return (
			<div className="afs-line__section">
				<div className="afs-line__section-head">
					<span className="tk-section-title">Bank transactions</span>
					<span className="tk-section-count">{rows.length}</span>
				</div>
				<div className="tk-ledger-entries__entry tk-ledger-entries__entry--head afs-line__entry--two-amounts">
					<span>Date</span><span>Description</span><span className="tk-ledger-entries__entry-amount">Debit</span><span className="tk-ledger-entries__entry-amount">Credit</span>
				</div>
				{rows.map(row => (
					<div key={row.id + (row.isTaxLeg ? "-tax" : "")} className="tk-ledger-entries__entry afs-line__entry--two-amounts">
						<span className="tk-ledger-entries__entry-date">{formatDrillDate(row.date)}</span>
						<span className="tk-ledger-entries__entry-text" title={row.description}>{row.description}{row.isTaxLeg && <span className="tk-ledger-entries__entry-memo">GST/HST</span>}</span>
						<span className="tk-ledger-entries__entry-amount">{row.debit ? formatDrillAmount(row.debit) : "—"}</span>
						<span className="tk-ledger-entries__entry-amount">{row.credit ? formatDrillAmount(row.credit) : "—"}</span>
					</div>
				))}
				<div className="tk-ledger-entries__entry tk-ledger-entries__entry--total afs-line__entry--two-amounts">
					<span /><span>Total</span>
					<span className="tk-ledger-entries__entry-amount">{formatDrillAmount(totalDebit)}</span>
					<span className="tk-ledger-entries__entry-amount">{formatDrillAmount(totalCredit)}</span>
				</div>
			</div>
		);
	};

	renderAdjustingEntries = ({ line }) => {
		return (
			<div className="afs-line__section">
				<div className="afs-line__section-head">
					<span className="tk-section-title">Adjusting entries</span>
					<span className="tk-section-count">{line.adjustingEntries.length}</span>
				</div>
				{line.adjustingEntries.length === 0 && <p className="afs-line__empty">None on this line.</p>}
				{line.adjustingEntries.map((entry, position) => (
					<div key={position} className="tk-ledger__row afs-line__row">
						<span className="afs-line__row-name">AJE {entry.entryNumber}{entry.description && <span className="afs-line__row-detail">{entry.description}</span>}</span>
						<span className="tk-ledger__amount">{entry.side === "DEBIT" ? formatDrillAmount(entry.amount) : "—"}</span>
						<span className="tk-ledger__amount">{entry.side === "CREDIT" ? formatDrillAmount(entry.amount) : "—"}</span>
					</div>
				))}
			</div>
		);
	};

	renderOneLine = opened => {
		const { detail, loading, loadError } = this.state;
		if (loading) return <p className="afs-line__empty">Reading what makes up this line…</p>;
		if (loadError) return <p className="afs-line__warning">{loadError}</p>;
		if (!detail || detail.lines.length === 0) return null;
		const line = detail.lines[0];
		return (
			<>
				{opened.reclassifiedFrom && (
					<p className="afs-line__note">Presented here because {line.displayName} holds a balance on the other side; the figures below are {line.displayName}'s, shown the way this line shows them.</p>
				)}
				{opened.presentedAs && (
					<p className="afs-line__note">This year's balance is on the other side, so the statement presents it as {this.describeLineName(opened.presentedAs)} and this line shows nothing for the year. The figures below are this account's own.</p>
				)}
				{this.renderHowItIsMade({ opened, line })}
				{detail.booksCameFromQuickBooks && this.renderQuickBooksAccounts({ opened, line })}
				{this.renderImportedRows({ line })}
				{this.renderTransactions({ line })}
				{this.renderAdjustingEntries({ line })}
			</>
		);
	};

	render() {
		const { onClose } = this.props;
		const { trail, panelWidth, detail } = this.state;
		const opened = this.showing();
		if (!opened) return null;
		const showsLines = opened.kind === "lines" || this.isNetIncome(opened);
		const firstLine = !showsLines && detail && detail.lines.length > 0 ? detail.lines[0] : null;
		const lineRead = firstLine ? AFSService.describeHowALineIsMade({ line: firstLine, presentedOnTheOtherSide: !!opened.reclassifiedFrom }) : null;
		const clickedFigureIsKnown = typeof opened.current === "number";
		const figuresFromTheLine = { current: lineRead ? lineRead.closing : null, prior: lineRead ? lineRead.prior : null };
		const figures = clickedFigureIsKnown ? { current: opened.current, prior: opened.prior } : figuresFromTheLine;

		return (
			<div className="afs-line" style={{ "--afs-line-width": panelWidth + "px" }} data-test="line-detail">
				<div className="afs-line__resize" onMouseDown={this.onResizeStart} title="Drag to resize" />
				<div className="afs-line__header">
					<div className="afs-line__header-top">
						<div className="afs-line__heading">
							{trail.length > 1 && (
								<button type="button" className="tk-text-action afs-line__back" onClick={this.onBack}>
									<Icon.ChevronLeft size={14} /> {trail[trail.length - 2].title}
								</button>
							)}
							<h3 className="afs-line__title">{opened.title}</h3>
						</div>
						<button type="button" className="afs-line__close" onClick={onClose} aria-label="Close"><Icon.X size={18} /></button>
					</div>
					{this.renderFigures(figures)}
				</div>
				<div className="afs-line__body">
					{showsLines ? this.renderLines(opened) : this.renderOneLine(opened)}
				</div>
			</div>
		);
	}
}

export default StatementLineDetailPanel;
